import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';

const CustomersList = ({ customers: initialCustomers, onCustomerSelect, onRefresh }) => {
  const [customers, setCustomers] = useState(initialCustomers || []);
  const [loading, setLoading] = useState(false);
  const [refreshingOpenAI, setRefreshingOpenAI] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!initialCustomers || initialCustomers.length === 0) {
      fetchCustomers();
    } else {
      setCustomers(initialCustomers);
    }
  }, [initialCustomers]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const url = searchTerm
        ? `/admin/customers?search=${encodeURIComponent(searchTerm)}`
        : '/admin/customers';
      const data = await apiRequest(url);
      if (data && data.success) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      fetchCustomers();
    }, 500);
  };

  const handleRefreshOpenAICosts = async () => {
    setRefreshingOpenAI(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await apiRequest('/admin/openai/process-all', {
        method: 'POST',
        body: JSON.stringify({ daysBack: 180 }),
      });
      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: data.message || `تم معالجة ${data.data?.customersProcessed || 0} عميل. تمت معالجة ${data.data?.totalProcessed || 0} تكلفة بقيمة ${data.data?.totalCost || 0}` 
        });
        // Refresh customers list to show updated balances
        fetchCustomers();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ أثناء معالجة تكاليف OpenAI' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
      console.error('Error refreshing OpenAI costs:', error);
    } finally {
      setRefreshingOpenAI(false);
    }
  };

  if (loading && customers.length === 0) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        جاري التحميل...
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">قائمة العملاء</h2>
          <div className="flex gap-2">
            <button 
              className="btn btn-info btn-sm" 
              onClick={handleRefreshOpenAICosts}
              disabled={refreshingOpenAI}
            >
              {refreshingOpenAI ? 'جاري المعالجة...' : 'تحديث رصيد OpenAI للجميع'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={fetchCustomers}>
              🔄 تحديث
            </button>
          </div>
        </div>

        {message.text && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
            <span>{message.type === 'success' ? '✅' : '❌'}</span>
            <span>{message.text}</span>
          </div>
        )}

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <input
            type="text"
            className="form-control"
            placeholder="بحث عن عميل..."
            value={searchTerm}
            onChange={handleSearch}
            style={{ maxWidth: '400px' }}
          />
        </div>

        {customers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👤</div>
            <div className="empty-state-title">لا يوجد عملاء</div>
            <div className="empty-state-text">ابدأ بإضافة عميل جديد</div>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>البريد الإلكتروني</th>
                  <th>رصيد AI</th>
                  <th>رصيد SMS</th>
                  <th>رصيد OpenAI</th>
                  <th>الحالة</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer._id}>
                    <td><strong>{customer.name}</strong></td>
                    <td>{customer.email}</td>
                    <td>{customer.balances?.aiCredits || 0}</td>
                    <td>{customer.balances?.smsCredits || 0}</td>
                    <td>{customer.balances?.openaiBalance || 0}</td>
                    <td>
                      <span className={`badge ${customer.isActive ? 'badge-success' : 'badge-danger'}`}>
                        {customer.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => onCustomerSelect(customer)}
                      >
                        عرض التفاصيل
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersList;
